// The Schedule of Work's cover page and summary (docs/SCHEDULE-OF-WORK.md).
import { HIGGINS_LOGO_ASPECT, HIGGINS_LOGO_PNG } from '@higgins/ui';
import { totalsOf, type ScheduleArea, type ScheduleOptions } from './model';
import { BRAND, CONTENT_WIDTH, PAGE, PdfPage, table, type Column } from './pdf-kit';
import type { LoadedPhoto } from './photos';
import { formatQuantity, formatWhole } from '../../components/surface-format';

export interface CoverDetails {
  jobName: string | null;
  client: string | null;
  site: string | null;
  jobNumber: string;
  estimator: string | null;
  preparedBy: string | null;
  /** DD/MM/YYYY. */
  date: string;
  coverNote: string;
  /** The option whose paint systems the surfaces list (#412); null when no surface has more than one. */
  paintSystemsFrom: string | null;
}

const COVER_PHOTO_MAX_HEIGHT = 95;
const COVER_LOGO_HEIGHT = 18;
const LABEL_WIDTH = 32;


/** A photo scaled to fit a box, keeping its shape. */
export function fit(photo: LoadedPhoto, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / photo.width, maxHeight / photo.height);
  return { width: photo.width * scale, height: photo.height * scale };
}

function details(page: PdfPage, d: CoverDetails) {
  const rows: [string, string | null][] = [
    ['Client', d.client],
    ['Site', d.site],
    ['Job No.', d.jobNumber],
    ['Estimator', d.estimator],
    ['Prepared by', d.preparedBy],
    ['Date', d.date],
    ['Paint systems', d.paintSystemsFrom && `As option ${d.paintSystemsFrom}`],
  ];
  for (const [label, value] of rows) {
    if (!value) continue;
    page.font(9, 'normal', BRAND.mid);
    page.doc.text(label, PAGE.margin, page.y + 4);
    const startY = page.y;
    page.paragraph(value, { size: 9.5, style: 'bold', x: PAGE.margin + LABEL_WIDTH, width: CONTENT_WIDTH - LABEL_WIDTH, gapAfter: 1 });
    page.y = Math.max(page.y, startY + 5.5);
  }
  page.y += 3;
}

const COVER_TEXT_SIZE = 9.5;
const BULLET = /^[•\-*]\s+/;

/** A cover-note paragraph; a heading line over "• " lines (the Internal and External areas, the paint systems) prints as a bold heading and bullets. */
function coverNoteParagraph(page: PdfPage, para: string) {
  const [first, ...rest] = para.split('\n');
  if (!rest.length || !rest.every((line) => BULLET.test(line)) || BULLET.test(first)) {
    page.paragraph(para, { size: COVER_TEXT_SIZE, gapAfter: 2.5 });
    return;
  }
  page.paragraph(first, { size: COVER_TEXT_SIZE, style: 'bold', gapAfter: 0.5 });
  for (const line of rest) page.bullet(line.replace(BULLET, ''), COVER_TEXT_SIZE);
  page.y += 2.2;
}

export function drawCover(page: PdfPage, d: CoverDetails, areas: ScheduleArea[], coverPhoto: LoadedPhoto | null) {
  const { doc } = page;
  doc.setFillColor(BRAND.orange);
  doc.rect(0, 0, PAGE.width, 2, 'F');
  doc.addImage(HIGGINS_LOGO_PNG, 'PNG', PAGE.margin, 9, COVER_LOGO_HEIGHT * HIGGINS_LOGO_ASPECT, COVER_LOGO_HEIGHT);
  page.y = 34;
  page.paragraph('SCHEDULE OF WORK', { size: 20, style: 'bold', gapAfter: 1 });
  if (d.jobName) page.paragraph(d.jobName, { size: 12, colour: BRAND.mid, gapAfter: 4 });
  doc.setFillColor(BRAND.orange);
  doc.rect(PAGE.margin, page.y, 30, 1, 'F');
  page.y += 5;

  if (coverPhoto) {
    const size = fit(coverPhoto, CONTENT_WIDTH, COVER_PHOTO_MAX_HEIGHT);
    doc.addImage(coverPhoto.dataUrl, 'JPEG', PAGE.margin + (CONTENT_WIDTH - size.width) / 2, page.y, size.width, size.height);
    page.y += size.height + 5;
  }
  details(page, d);
  if (d.coverNote.trim()) {
    for (const para of d.coverNote.trim().split(/\n{2,}/)) coverNoteParagraph(page, para);
    page.y += 2;
  }
  const excluded = areas.filter((a) => a.exclusions);
  if (excluded.length) {
    page.bar('EXCLUDED FROM SCOPE', BRAND.purple);
    for (const a of excluded) {
      page.paragraph(a.name, { size: 9, style: 'bold', gapAfter: 0.5 });
      page.paragraph(a.exclusions ?? '', { size: 9, gapAfter: 2 });
    }
  }
}

function summaryColumns(options: ScheduleOptions): Column[] {
  const numbers = [options.showM2 && 'm²', options.showHours && 'Hours', options.showLitres && 'Litres'].filter((c): c is string => Boolean(c));
  const numberWidth = 28;
  return [{ label: 'Area', width: CONTENT_WIDTH - numbers.length * numberWidth }, ...numbers.map((label) => ({ label, width: numberWidth, align: 'right' as const }))];
}

function summaryCells(name: string, t: { m2: number; hours: number; litres: number }, options: ScheduleOptions): string[] {
  return [name, ...(options.showM2 ? [formatWhole(t.m2)] : []), ...(options.showHours ? [formatQuantity(t.hours)] : []), ...(options.showLitres ? [formatWhole(t.litres)] : [])];
}

/** One table of every area, internal then external, each side totalled, then the grand total. */
export function drawSummary(page: PdfPage, areas: ScheduleArea[], options: ScheduleOptions) {
  if (options.hoursSource === 'none' || !(options.showM2 || options.showHours || options.showLitres) || areas.length === 0) return;
  const label = { measured: 'measured', approved: 'approved hours' }[options.hoursSource];
  page.ensure(40);
  page.bar(`SUMMARY (${label}; hours include down time)`);
  const rows: { cells: string[]; bold?: boolean; shade?: boolean }[] = [];
  for (const side of ['Internal', 'External']) {
    const list = areas.filter((a) => a.side === side);
    if (!list.length) continue;
    list.forEach((a, i) => rows.push({ cells: summaryCells(a.name, a.totals, options), shade: i % 2 === 1 }));
    rows.push({ cells: summaryCells(`${side} total`, totalsOf(list), options), bold: true });
  }
  rows.push({ cells: summaryCells('Total', totalsOf(areas), options), bold: true });
  table(page, summaryColumns(options), rows);
}
